import { Injectable, inject } from '@angular/core';
import { Subscription } from 'rxjs';
// Pyodide is loaded on demand in init() (EHQ-91) so its wrapper stays out of
// the initial bundle; only the types are needed at compile time.
import {
  // FILE_EXTENSIONS,
  LOAD_SNAPSHOT,
  RELOAD_FILES,
  RELOAD_NOTEBOOK_FILES,
  STEPS_NAMESPACE,
} from '../../constants/general.constants';
import { GeneralHelpers } from '../../helpers/general.helper';
import { Chunk } from '../../interfaces/step/step.interface';
import {
  ChunkContext,
  ExecutionContext,
} from '../../interfaces/chunk/chunk-context.interface';
import { NotebookFile } from '../../interfaces/file.interface';
import { FileService } from '../file/file.service';
import { NotebookVariablesService } from '../notebook-variables/notebook-variables.service';
import { MessageService } from '../message/message.service';
import { RenderService } from '../render/render.service';
import { CanvasService } from '../canvas/canvas.service';
import { ArrowWebsocketService } from '../arrow-websocket/arrow-websocket.service';
import { ObjectStorageFileService } from '../object-storage/object-storage-file.service';
import { NotebookFilesService } from '../notebook-files/notebook-files.service';
import { NotebookDefaultFilesService } from '../notebook-default-file/notebook-default-file.service';
import { CacheService } from '../cache/cache.service';
import { FileUnifiedService } from '../file-unified/file-unified.service';
import { ConvertService } from '../convert/convert.service';
import { DuckDbService } from '../duck-db/duck-db.service';
import { WasmModuleService } from '../wasm/wasm-module/wasm-module.service';
import { SimpleUIService } from '../simple-ui/simple-ui.service';
import { ProjectVariablesService } from '../project-variables/project-variables.service';
import { CdnModuleService } from '../cdn-module/cdn-module.service';
import { FatClientService } from '../fat-client/fat-client.service';
import { BoxService } from '../box/box.service';
import { ProjectStateService } from '../project-state/project-state.service';
import { RoutineService } from '../routine/routine.service';
// python modules
import { PythonModulesService } from './core/python-modules.service';
import { PythonRuntimeService } from './core/python-runtime.service';
import { PythonModuleManager } from './core/python-module-manager.service';
import { PythonEnvironmentHelper } from './helpers/python-environment.helper';
import { PythonHelper } from './helpers/python.helper';
import { PyodideFileJournal } from './journal/python-journal.helper';
import { CleanupResult, PYTHON_DIRECTORIES } from './interfaces/python.interface';
import { PythonEHQBridge } from './bridges/python-ehq-bridge.helper';
import { EHQServicesConfig } from './interfaces/python-ehq.interface';
import { PythonFileSystemHelper } from './filesystem/python-filesystem.helper';
import { FileSystemProxy } from './filesystem/python-filesystem-proxy.helper';
import { FileSystemProxyConfig } from './interfaces/python-filesystem-proxy.interface';
import { PythonPackageManagerService } from './packages/python-package-manager.service';
import { PythonCodeValidator } from './code/python-code-validator.helper';
import { PythonCodeFormatter } from './code/python-code-formatter.helper';
import { PythonCodeLinter, PyLintDiagnostic } from './code/python-code-linter.helper';
import { PythonVariablesHelper } from './execution/python-variables.helper';
import { PythonImportsHelper } from './execution/python-imports.helper';
import { StepService } from '../step/step.service';
import { FileDataService } from '../file-data/file-data.service';
import { LocalforageService } from '../localforage/localforage.service';
import { AppStateService } from '../app-state/app-state.service';
import { OAuthService } from 'angular-oauth2-oidc';
import { LlmBridgeService } from '../llm/llm-bridge.service';
import { ServiceCallBridgeService } from '../service-call/service-call-bridge.service';
import { ConnectionService } from '../connection/connection.service';
import { StepTypeService } from '../step-type/step-type.service';
import { SecretsService } from '../secrets/secrets.service';
import { PushDestinationService } from '../push-destination/push-destination.service';
import { DeploymentInfoService } from '../deployment-info/deployment-info.service';
import { LlmService } from '../llm/llm.service';
import { IntrospectionBridgeService } from './bridges/introspection-bridge.service';
import { HeavyServicesReadinessHelper } from '../../helpers/heavy-services-readiness.helper';
import { PythonContextManager } from './context/python-context.manager';
import { PythonCleanupHelper } from './cleanup/python-cleanup.helper';
import { PythonPackageHelper } from './packages/python-package.helper';
import { PythonFileLoadingHelper } from './sync/python-file-loading.helper';
import { consumeControlHalt, resetControlHalt } from '../../helpers/control-halt.helper';

// Env
import * as AppConfigModule from '../../../../config-loader';

/** How many failed files the step's load warning names before "+N more". */
const MAX_REPORTED_LOAD_FAILURES = 5;

@Injectable({
  providedIn: 'root',
})
export class PythonService {
  private readonly notebookVariablesService = inject(NotebookVariablesService);
  private readonly messageService = inject(MessageService);
  private readonly notebookFilesService = inject(NotebookFilesService);
  private readonly notebookDefaultFilesService = inject(NotebookDefaultFilesService);
  private readonly fileService = inject(FileService);
  private readonly cacheService = inject(CacheService);
  private readonly fileUnifiedService = inject(FileUnifiedService);
  private readonly projectVariablesService = inject(ProjectVariablesService);
  private readonly stepService = inject(StepService);
  private readonly renderService = inject(RenderService);
  private readonly canvasService = inject(CanvasService);
  private readonly arrowWebsocketService = inject(ArrowWebsocketService);
  private readonly objectStorageFileService = inject(ObjectStorageFileService);
  private readonly duckDbService = inject(DuckDbService);
  private readonly simpleUIService = inject(SimpleUIService);
  private readonly convertService = inject(ConvertService);
  private readonly localforageService = inject(LocalforageService);
  private readonly fatClientService = inject(FatClientService);
  private readonly boxService = inject(BoxService);
  private readonly stateService = inject(ProjectStateService);
  private readonly routineService = inject(RoutineService);
  private readonly cdnModuleService = inject(CdnModuleService);
  private readonly pythonModulesService = inject(PythonModulesService);
  private readonly pythonRuntime = inject(PythonRuntimeService);
  private readonly moduleManager = inject(PythonModuleManager);
  private readonly fileDataService = inject(FileDataService);
  private readonly appStateService = inject(AppStateService);
  private readonly oauthService = inject(OAuthService);
  private readonly connectionService = inject(ConnectionService);
  private readonly stepTypeService = inject(StepTypeService);
  private readonly secretsService = inject(SecretsService);
  private readonly pushDestinationService = inject(PushDestinationService);
  private readonly deploymentInfoService = inject(DeploymentInfoService);
  private readonly llmService = inject(LlmService);
  private readonly wasmModuleService = inject(WasmModuleService);

  // Service call wrapper — adapts ServiceCallBridgeService to the (args: unknown[])
  // signature expected by the CUSTOM_METHODS_IMPORT['ehq.call'] entry. Resolved
  // through constants pathway (single source of truth).
  //
  // The IIFE captures one ServiceCallBridgeService instance for the lifetime of
  // PythonService. ServiceCallBridgeService is stateless — it reads token via
  // `oauth.getAccessToken()` and projectId via `appState.projectId()` on each
  // `call()`, so a singleton is behaviourally identical to per-call instantiation
  // but avoids the allocation overhead of the JS-bridge pattern.
  private readonly serviceCallService = (() => {
    const bridge = new ServiceCallBridgeService(this.oauthService, this.appStateService);
    return { call: bridge.callStep };
  })();

  private readonly environment = AppConfigModule.appConfig.env;

  // Reuse the bridge's auto-derived whitelist as single source of truth.
  // Mirrors PythonEHQBridge.JS_EXTERNAL_MODULES_NAMESPACES, which is computed
  // from CUSTOM_METHODS_EXPORT/IMPORT in additional-methods.constants.ts.
  protected static jsExternalModulesNamespaces = PythonEHQBridge.JS_EXTERNAL_MODULES_NAMESPACES;
  public python: any;
  private chunkData: any;

  /** Flag to track first output of current execution for clearing old output */
  private firstOutputOfRun = true;

  /**
   * ✅ PROTECTION: Check if context is valid and safe to use
   * Delegates to PythonHelper
   */
  private isContextValid(context: any): boolean {
    return PythonHelper.isContextValid(context);
  }
  private isLoaded: boolean = false;
  // Ensure we initialize the runtime at most once and allow awaiting it from anywhere
  private initializationPromise?: Promise<void>;
  private readonly messageShown: boolean = false;

  // Directory paths - managed by PythonEnvironmentHelper
  private get workingDir(): string {
    return PythonEnvironmentHelper.getDirectoryPaths().workingDir;
  }

  // Subdirectory getters for filesystem restructure
  private get filesSubdir(): string {
    return PYTHON_DIRECTORIES.FILES_SUBDIR;
  }

  private get defaultSubdir(): string {
    return PYTHON_DIRECTORIES.DEFAULT_SUBDIR;
  }

  private get projectSubdir(): string {
    return PYTHON_DIRECTORIES.PROJECT_SUBDIR;
  }
  private micropip: any;
  private manualReloadRequested: boolean = true;  // Match fe813d8 default (notebookFilesShouldBeReloaded = true)
  private notebookFiles: NotebookFile[] = [];
  // SCRUM-166: Flag to distinguish self-triggered reloads from external ones
  // When true, RELOAD_NOTEBOOK_FILES messages are suppressed to avoid re-fetching files
  // that were just synced by the current Python service execution
  private suppressReloadDuringExecution: boolean = false;

  // File operations journal for safe cleanup - primary tracking mechanism
  private readonly fileJournal = new PyodideFileJournal();


  // EHQ Bridge for Python-JavaScript integration
  private ehqBridge?: PythonEHQBridge;
  /** Torn down when the bridge is re-created (e.g. Pyodide init retry). */
  private introspectionBridge?: IntrospectionBridgeService;

  // Helper services for modular functionality
  private fileSystemHelper?: PythonFileSystemHelper;
  private fsProxy?: FileSystemProxy;
  private packageManager?: PythonPackageManagerService;
  private codeValidator?: PythonCodeValidator;
  private codeFormatter?: PythonCodeFormatter;
  private codeLinter?: PythonCodeLinter;
  private variablesHelper?: PythonVariablesHelper;
  private importsHelper?: PythonImportsHelper;
  private contextManager?: PythonContextManager;
  private cleanupHelper?: PythonCleanupHelper;
  private packageHelper?: PythonPackageHelper;
  private fileLoadingHelper?: PythonFileLoadingHelper;

  // Track loaded packages to avoid duplicate loading and verification
  private loadedPackages: Set<string> = new Set();

  // Cache JS module names (camelCase + snake_case) for filtering
  private jsModuleNamesCache?: Set<string>;

  // Track current run for file sync optimization
  private currentRunStartTimestamp?: number;

  // private fileList: ProjectFileItem[] = [];
  // ─────────────────────────────────────────────────────────────────────

  // Message subscription
  private readonly messageSubscription!: Subscription;

  constructor() {
    this.messageSubscription = this.messageService
      .getMessage()
      .subscribe(async (message: any) => {
        if ([LOAD_SNAPSHOT, RELOAD_NOTEBOOK_FILES, RELOAD_FILES].some((action) => action === message?.text)) {
          // SCRUM-166: Skip self-triggered reloads during step execution to prevent
          // unnecessary re-fetch of files that were just synced.
          // External reloads (user upload, version switch, snapshot load) still trigger reload.
          if (this.suppressReloadDuringExecution && message?.text === RELOAD_NOTEBOOK_FILES) {
            return;
          }
          this.manualReloadRequested = true;
        }
      });
  }

  async init() {
    // Prevent multiple initializations
    if (this.initializationPromise) {
      return this.initializationPromise;
    }

    this.initializationPromise = (async () => {
      if (!this.python) {
        // Dynamic import keeps the pyodide wrapper off the critical path; the
        // wasm + stdlib download starts here, after first paint (EHQ-91).
        // fullStdLib must stay true - step code can import any stdlib module.
        const { loadPyodide } = await import('pyodide');
        this.python = await loadPyodide({
          indexURL: `https://cdn.jsdelivr.net/pyodide/${this.environment.pyodideVersion}/full/`,
          fullStdLib: true,
        });
      }

      // Clear the file journal for fresh start
      this.fileJournal.clear();

      // Clear loaded packages tracking for fresh start
      this.loadedPackages.clear();

      // Create and apply FileSystem Proxy for automatic file journaling
      // Filesystem Restructure: Subdirectories are persistent, root files are temporary
      const proxyConfig: FileSystemProxyConfig = {
        // "Delete by Default" philosophy:
        // - Root files (/home/pyodide/*.csv) are TEMPORARY (not persisted to database)
        // - Subdirectory files (/home/pyodide/files/*.csv) are PERSISTENT
        // persistenceRules.paths determines which directories persist
        defaultPersistent: true,  // Root files are temporary (PROT-810 fix)

        // Persistence rules: Auto-persist files in structured subdirectories
        // This solves PROT-777 - user-created subdirectories under files/ are persistent
        persistenceRules: {
          paths: [
            `${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.FILES_SUBDIR}`,    // Persistent: files/**
            `${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.DEFAULT_SUBDIR}`,  // Persistent: default/**
            `${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.PROJECT_SUBDIR}`   // Persistent: project/**
          ]
        },

        // Journal everything (no path exclusions)
        excludePaths: [],

        // Disable debug logging in production
        enableLogging: true
      };

      // Create subdirectory structure BEFORE proxy wrapping (Filesystem Restructure)
      // This ensures originalFS in proxy will see the directories
      try {
        this.python.FS.mkdirTree(`${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.FILES_SUBDIR}`);
        this.python.FS.mkdirTree(`${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.DEFAULT_SUBDIR}`);
        this.python.FS.mkdirTree(`${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.PROJECT_SUBDIR}`);

        // NOTE: chmod not used - read-only enforcement via FileSystemProxy interceptors
        // Pyodide MEMFS chmod() sets inode.mode which BLOCKS write operations at low level,
        // even when permissionsEnabled=false. This caused errno 2 (ENOENT) on file writes.
        // Solution: Use TypeScript-level interceptors (interceptWriteFile/interceptOpen) instead.

        // Create root-level symlinks so absolute paths work the same as relative paths.
        // e.g. /files/foo.txt → /home/pyodide/files/foo.txt
        // e.g. /workflow/foo.txt → /home/pyodide/foo.txt (enoch compat)
        this.python.FS.symlink(PYTHON_DIRECTORIES.WORKING_DIR, '/workflow');
        this.python.FS.symlink(`${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.FILES_SUBDIR}`, `/${PYTHON_DIRECTORIES.FILES_SUBDIR}`);
        this.python.FS.symlink(`${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.DEFAULT_SUBDIR}`, `/${PYTHON_DIRECTORIES.DEFAULT_SUBDIR}`);
        this.python.FS.symlink(`${PYTHON_DIRECTORIES.WORKING_DIR}/${PYTHON_DIRECTORIES.PROJECT_SUBDIR}`, `/${PYTHON_DIRECTORIES.PROJECT_SUBDIR}`);
      } catch (error: any) {
        console.warn('Python service: failed to create working directories', error);
      }

      // Create FileSystem Proxy AFTER directories are created
      this.fsProxy = new FileSystemProxy(this.python.FS, this.fileJournal, proxyConfig);
      this.python.FS = this.fsProxy.wrap(this.python.FS);
      // Python's file calls go straight to the FS object, not through the wrap (EHQ-545)
      this.fsProxy.installFsGuards(this.python.ERRNO_CODES);

      // Install Python-level hooks for file operations
      this.fsProxy.installPythonHooks(this.python);

      // Initialize Python environment with project isolation and security
      const _projectId = this.appStateService.projectId();

      // Phase 2: Simplified environment configuration
      await PythonEnvironmentHelper.configurePythonEnvironment(this.python);

      // Real-time log transformer is now installed by codeFormatter.initialize()
      // which is called in initializeHelpers()

      // Load required packages
      await this.python.loadPackage('micropip');
      this.micropip = this.python.pyimport('micropip');

      // Initialize all services
      await this.pythonRuntime.initialize(
        this.python, PythonService.jsExternalModulesNamespaces
      );

      // Initialize helpers first
      await this.initializeHelpers();

      await this.moduleManager.initialize(
        this.python, PythonService.jsExternalModulesNamespaces, this.fileJournal
      );
      await this.pythonModulesService.initialize(
        this.python, PythonService.jsExternalModulesNamespaces
      );

      // Initialize EHQ Bridge
      this.initializeEHQBridge();

      // WASM host-api needs no setup here: WasmModuleService owns its transport and
      // reads the current project per call (EHQ-76).

      // Setup WASM module Python bindings if any modules are loaded
      const wasmModules = this.wasmModuleService?.listModules() ?? [];
      if (wasmModules.length > 0 && this.ehqBridge) {
        await this.ehqBridge.setupWasmModuleBindings(this.wasmModuleService);
      }

      // Helper services already initialized above (before moduleManager)

      await this.cleanupProject();

      this.isLoaded = true;
    })();

    try {
      await this.initializationPromise;
    } catch (e) {
      // Reset so future attempts can retry
      this.initializationPromise = undefined;
      this.isLoaded = false;
      throw e;
    }
  }

  /**
   * Execute Python code with automatic permission management
   *
   * PERMISSION FLOW (centralized in FileSystemProxy):
   * 1. disablePermissions() - allow system to write to ALL directories
   * 2. syncProjectFiles() - populate ./project/ and ./default/ without restrictions
   * 3. enablePermissions() - enforce read-only protection
   * 4. executePythonCode() - user code restricted to ./files/ only
   *
   * This ensures system can populate read-only directories during initialization,
   * while user Python code is restricted to writable ./files/ directory.
   *
   * @see FileSystemProxy for detailed permission architecture
   */
  async run(context: ChunkContext): Promise<Chunk | undefined> {
    try {
      // Reset first-output flag for this execution
      this.firstOutputOfRun = true;

      // STEP 0: wait - Ensure service is ready (consolidates steps 1-13)
      await HeavyServicesReadinessHelper.waitForReadiness();

      // STEP 1: INITIALIZATION - Ensure service is ready (consolidates steps 1-13)
      await this.ensureInitialized(context);

      // STEP 2: FILE SYNCHRONIZATION - Sync project files (consolidates steps 14-22, 38-45)
      // Disable permissions during system file loading
      this.fsProxy?.disablePermissions();

      // Drop failures left by an earlier sync that threw before reporting them,
      // so this step only warns about its own.
      this.fileLoadingHelper?.takeFailures();
      await this.syncProjectFiles();
      this.reportFileLoadFailures(context);

      // STEP 3: CODE PREPARATION - Prepare and validate code (consolidates steps 23-36, 41-43)
      // Enable permissions before user code execution
      this.fsProxy?.enablePermissions();

      // EHQ-104: Mark run-boundary AFTER system file load so getMountedFiles()
      // only returns files touched by user/Python code in this run (not files
      // just loaded from the backend into Pyodide).
      this.currentRunStartTimestamp = Date.now();

      const preparedCode = await this.prepareCode(context);

      if (!preparedCode) {
        return undefined;
      }

      // Store for cleanup
      this.lastProcessedContent = preparedCode;

      // Setup output streaming before execution
      this.setupOutputStreaming(context);

      // STEP 4: EXECUTION - Execute the prepared code (consolidates steps 37, 46-48)
      const result = await this.executePythonCode(preparedCode, context);

      // Flush stdout/stderr to ensure all batched print() output is delivered
      // before we check hadStreamedOutput. Without this, late print() calls
      // (especially after bridge calls like ehq.displayTable) may still be
      // buffered in Pyodide and not yet delivered to our batched handler.
      try {
        await this.python.runPythonAsync('import sys; sys.stdout.flush(); sys.stderr.flush()');
      } catch {
        // Ignore flush errors — non-critical
      }

      // Capture whether print() streamed output during execution,
      // then prevent late batched callbacks from clearing result
      const hadStreamedOutput = !this.firstOutputOfRun;
      this.firstOutputOfRun = false;

      // STEP 5: RESULT FORMATTING - Format and return result (consolidates steps 49-59)
      // SCRUM-166: Suppress self-triggered RELOAD_NOTEBOOK_FILES during file sync
      // to prevent next step from re-fetching all files unnecessarily
      this.suppressReloadDuringExecution = true;
      const hasSyntaxError = !!(await this.validateSyntax(preparedCode)).error;
      const processedResult = await this.processResult(result, context, hasSyntaxError, hadStreamedOutput);

      return processedResult;

    } catch (error: any) {
      // Handle any errors that weren't caught in the individual methods
      return this.handleExecutionError(error, context);
    } finally {
      // Cleanup streaming handlers
      this.cleanupOutputStreaming();// Always cleanup
      this.chunkData = null;
      // SCRUM-166: Re-enable external reload detection after execution completes
      this.suppressReloadDuringExecution = false;
    }
  }

  /**
   * SCRUM-166: Check if the file list has changed compared to cached notebookFiles
   * Compares by file names and count to detect additions/removals without deep comparison
   */
  private hasFileListChanged(newFiles: NotebookFile[]): boolean {
    if (this.notebookFiles.length !== newFiles.length) {
      return true;
    }
    const oldNames = new Set(this.notebookFiles.map(f => f.name));
    return newFiles.some(f => !oldNames.has(f.name));
  }

  /**
   * Determines if the chunk context has changed significantly enough to require unmounting files
   * @param newContext The new chunk context to compare against the current one
   * @returns boolean indicating if the context has changed significantly
   */
  private hasContextChanged(newContext: ChunkContext, previousContext?: ExecutionContext | ChunkContext): boolean {
    return this.contextManager!.hasContextChanged(newContext, previousContext);
  }

  /**
   * Check what aspects of the context have changed (project, workflow, version, snapshot)
   * Returns an object indicating what changed and what type of reload is needed
   */
  private checkContextChanges() {
    return this.contextManager!.checkContextChanges();
  }

  /**
   * Extract package name from wheel filename
   * Follows existing patterns for file processing
   */

  /**
   * Check if workflow or project has changed and trigger cleanup
   */
  private async checkAndHandleContextChange(): Promise<boolean> {
    return await this.contextManager!.checkAndHandleContextChange(
      async () => {
        await this.cleanupProject();
      },
      this.fileJournal,
      this.loadedPackages
    );
  }


  /**
   * Simple cleanup using journal-based approach
   * Delegates to PythonCleanupHelper
   */
  async cleanupProject(): Promise<CleanupResult> {
    return await this.cleanupHelper!.cleanupProject(this.loadedPackages);
  }

  /**
   * Unmounts files that exist in this.notebookFiles but not in the provided notebookFiles argument
   * Delegates to PythonCleanupHelper
   */
  private async unmountMissingFiles(notebookFiles: NotebookFile[]): Promise<void> {
    await this.cleanupHelper!.unmountMissingFiles(
      this.notebookFiles,
      notebookFiles,
      this.workingDir,
      this.isLoaded
    );
  }

  /**
   * Get content with global variables replaced
   * Delegates to PythonVariablesHelper
   */
  private getContentWithVariablesReplaced(content: string) {
    return this.variablesHelper!.getContentWithVariablesReplaced(
      content,
      this.projectVariablesService
    );
  }

  /**
   * Get JS module names in both camelCase and snake_case for filtering
   * Delegates to PythonEHQBridge
   */
  private getJSModuleNamesSet(): Set<string> {
    // Use cache wrapper object for state persistence
    const cacheWrapper = { value: this.jsModuleNamesCache };
    const result = PythonEHQBridge.getJSModuleNamesSet(cacheWrapper);
    this.jsModuleNamesCache = cacheWrapper.value;
    return result;
  }

  private async syncPyodideFilesToBackend(files: Map<string, string>): Promise<void> {
    if (files.size === 0) return;

    // Delegate to helper
    await this.fileSystemHelper!.syncFilesToBackend(
      files,
      {
        python: this.python,
        cacheService: this.cacheService,
        notebookDefaultFilesService: this.notebookDefaultFilesService,
        appStateService: this.appStateService,
        messageService: this.messageService,
        notebookFiles: this.notebookFiles
      },
      this.workingDir
    );
  }

  /**
   * Process loading packages from imports in the provided content
   * Delegates to PythonPackageHelper
   */
  private async processLoadingPackagesFromImports(content: string, filePath?: string) {
    const jsModules = this.getJSModuleNamesSet();
    await this.packageHelper!.processLoadingPackagesFromImports(content, filePath, jsModules);
  }


  /**
   * Process imports from main content and all .py files in working directory
   * Delegates to PythonPackageHelper
   */
  private async processAllImports(content: string) {
    const jsModules = this.getJSModuleNamesSet();
    await this.packageHelper!.processAllImports(content, jsModules);
  }

  /**
   * Add global variables from notebook
   * Delegates to PythonVariablesHelper
   */
  private addGlobalVariables(chunkData: Chunk) {
    const result = this.variablesHelper!.addGlobalVariables(
      chunkData,
      this.notebookVariablesService,
      this.projectVariablesService
    );
    // Ensure getJobArguments() sees variables injected into `data` for this run
    this.ehqBridge?.refreshJobArgumentsBinding();
    return result;
  }

  private getMountedFiles(): Map<string, string> {
    return this.cleanupHelper!.getMountedFiles(
      this.currentRunStartTimestamp,
      this.workingDir
    );
  }

  /**
   * Initialize EHQ Bridge for Python-JavaScript integration
   */
  private initializeEHQBridge(): void {
    this.introspectionBridge?.dispose();
    this.introspectionBridge = new IntrospectionBridgeService(
      this.stepService,
      this.stepTypeService,
      this.connectionService,
      this.appStateService,
      this.oauthService,
      this.secretsService,
      this.pushDestinationService,
      this.deploymentInfoService,
      this.llmService,
    );

    // Create services config for EHQ Bridge
    const servicesConfig: EHQServicesConfig = {
      notebookVariablesService: this.notebookVariablesService,
      messageService: this.messageService,
      notebookFilesService: this.notebookFilesService,
      fileService: this.fileService,
      cacheService: this.cacheService,
      fileUnifiedService: this.fileUnifiedService,
      projectVariablesService: this.projectVariablesService,
      stepService: this.stepService,
      fileDataService: this.fileDataService,
      renderService: this.renderService,
      canvasService: this.canvasService,
      arrowWebsocketService: this.arrowWebsocketService,
      objectStorageFileService: this.objectStorageFileService,
      duckDbService: this.duckDbService,
      simpleUIService: this.simpleUIService,
      routineService: this.routineService,
      convertService: this.convertService,
      fatClientService: this.fatClientService,
      boxService: this.boxService,
      stateService: this.stateService,
      llmBridgeService: new LlmBridgeService(this.oauthService, this.appStateService),
      introspectionBridgeService: this.introspectionBridge,
      // Resolves [EHQ, 'call', 'serviceCallService', 'call'] via constants pathway.
      // Wraps a singleton ServiceCallBridgeService captured in the field's IIFE.
      serviceCallService: this.serviceCallService,
    };

    // Initialize EHQ Bridge
    this.ehqBridge = new PythonEHQBridge({
      python: this.python,
      services: servicesConfig,
      chunkContext: undefined,  // Context will be updated later when run() is called
      enableLogging: false // Can be configured based on environment
    });

    // Initialize the bridge
    this.ehqBridge.initializeBridge();
  }

  /**
   * Initialize all helper services
   */
  private async initializeHelpers(): Promise<void> {

    // Initialize context manager for state tracking
    this.contextManager = new PythonContextManager(
      this.appStateService
    );

    // Initialize cleanup helper
    this.cleanupHelper = new PythonCleanupHelper(
      this.python,
      this.fileJournal,
      this.localforageService
    );

    //Initialize importsHelper FIRST - needed by packageHelper!
    // This must be created BEFORE packageHelper to prevent undefined reference
    this.importsHelper = new PythonImportsHelper(this.python, {
      enableLogging: false,
      checkAvailability: true
    });

    // Initialize variablesHelper (independent, no dependencies)
    this.variablesHelper = new PythonVariablesHelper(this.python, {
      enableLogging: false,
      trackVariableChanges: true
    });

    // Initialize filesystem helper BEFORE other helpers that depend on it
    // This must be created before packageHelper and fileLoadingHelper
    this.fileSystemHelper = new PythonFileSystemHelper(
      this.python,
      this.micropip,
      {
        workingDir: this.workingDir, // /home/pyodide (root with subdirectories)
        enableLogging: false,
        enforceProjectIsolation: true,
        maxFileSize: 50 * 1024 * 1024, // 50MB
        maxTotalFileSize: 250 * 1024 * 1024 // 250MB across workflow and project files (EHQ-548)
      },
      this.fileJournal,
      undefined,
      this.fsProxy,  // PROT-810: Pass fsProxy for trusted operations (system loading)
      this.localforageService  // Hash-based write optimization
    );

    // Initialize package helper (depends on importsHelper AND fileSystemHelper!)
    this.packageHelper = new PythonPackageHelper(
      this.python,
      this.fileJournal,
      this.importsHelper,  // ← NOW DEFINED! Created above
      this.fileService,
      this.fileSystemHelper,
      this.loadedPackages,
      this.workingDir
    );

    // Initialize file loading helper
    this.fileLoadingHelper = new PythonFileLoadingHelper(
      this.fileSystemHelper!,
      this.packageHelper!,
      this.workingDir
    );

    // Initialize package manager
    this.packageManager = new PythonPackageManagerService();
    // Initialize with runtime dependencies
    this.packageManager.initialize(this.python, this.micropip, this.fileJournal, this.cdnModuleService);

    // Initialize code helpers
    this.codeValidator = new PythonCodeValidator(this.python, {
      enableLogging: false,
      enableSuggestions: true
    });

    this.codeFormatter = new PythonCodeFormatter(
      this.python,
      this.micropip,
      this.cdnModuleService,
      {
        useBlack: true,
        enableLogging: false,
        fallbackToBasic: true
      }
    );

    // Initialize Black formatter immediately
    await this.codeFormatter.initialize();

    // Initialize pyflakes linter (installs the wheel in the background so the
    // first in-editor lint doesn't pay the install round-trip). Non-fatal.
    this.codeLinter = new PythonCodeLinter(this.python, this.micropip);
    this.codeLinter.initialize().catch((error) => {
      console.warn('Python service: pyflakes linter init failed', error);
    });

    // Execution helpers (importsHelper, variablesHelper) already initialized above (moved before packageHelper)
    // fileSystemHelper already initialized above (moved before packageHelper)
  }


  private registerExternalJSModules(content: string): string {
    if (!this.ehqBridge) {
      return content;
    }

    // Get current chunk context from manager and update bridge
    const currentContext = this.contextManager?.getCurrentChunkContext();
    if (currentContext) {
      this.ehqBridge.updateChunkContext(currentContext);
    }

    // Use bridge to register modules
    return this.ehqBridge.registerExternalJSModules(content);
  }

  private unregisterExternalJSModules(content: string): void {
    if (!this.ehqBridge) {
      return;
    }

    // Use bridge to unregister modules
    this.ehqBridge.unregisterExternalJSModules(content);
  }

  // Real-time logging methods moved to PythonCodeFormatter:
  // - installRealTimeLogTransformer() → PythonCodeFormatter.installRealTimeLogTransformer()
  // - transformForRealTimeLogging() → PythonCodeFormatter.transformForRealTimeLogging()
  // - injectPythonLoggerSetup() → PythonCodeFormatter.injectLoggerSetup()
  // - getPythonLoggerSetupCode() → PythonCodeFormatter.getLoggerSetupCode()

  private configurePythonLoggerForDebugger(): void {
    try {
      // Ensure `ehq` JS module is available before importing in Python bootstrap.
      this.registerExternalJSModules('ehq.log');
      this.python.runPython(PythonCodeFormatter.getLoggerSetupCode());
      // Set DEBUG level for debugger mode (normal execution uses INFO)
      this.python.runPython('import logging; logging.getLogger().setLevel(logging.DEBUG)');
    } catch (error) {
      console.warn('Python service: logger bootstrap failed in debugger mode', error);
    }
  }

  /**
   * Format code using PythonCodeFormatter helper
   */
  async formatCode(code: string): Promise<string> {
    if (!this.isLoaded || !this.codeFormatter) {
      return this.basicFormatCode(code);
    }

    try {
      const result = await this.codeFormatter.formatCode(code);

      if (result.success && result.formattedCode !== undefined && result.formattedCode !== null) {
        return result.formattedCode;
      } else {
        return this.basicFormatCode(code);
      }
    } catch (error) {
      console.warn('Python service: formatCode failed, using basic formatter', error);
      return this.basicFormatCode(code);
    }
  }

  /**
   * Lint Python code with pyflakes via PythonCodeLinter helper.
   *
   * Returns [] when the runtime isn't loaded yet — and kicks off init() so a
   * subsequent lint pass (the CM linter re-runs on the next edit) will have a
   * warm runtime. Never throws: linting must not break the editor. (EHQ-108)
   */
  async lintCode(code: string): Promise<PyLintDiagnostic[]> {
    if (!code || code.trim() === '') {
      return [];
    }
    if (!this.isLoaded || !this.codeLinter) {
      // Warm the runtime in the background; diagnostics appear on a later pass.
      // Swallow init errors here — linting is non-fatal and real load failures
      // surface through the normal init path, not the editor lint loop.
      this.init().catch(() => { /* non-fatal */ });
      return [];
    }
    try {
      return await this.codeLinter.lint(code);
    } catch (error) {
      console.warn('Python service: lintCode failed', error);
      return [];
    }
  }

  /**
   * Validate Python code syntax using PythonCodeValidator helper
   */
  async validateSyntax(code: string): Promise<{ isValid: boolean; error?: string; suggestions?: string[] }> {
    if (!this.isLoaded || !this.codeValidator) {
      return { isValid: true };
    }

    try {
      const result = await this.codeValidator.validateSyntax(code);
      return {
        isValid: result.isValid,
        error: result.error,
        suggestions: result.suggestions
      };
    } catch (error: any) {
      console.warn('Python service: validateSyntax failed, returning valid', error);
      return { isValid: true };
    }
  }

  /**
   * Verify package installation by attempting to import it
   */

  /**
   * Say on the step which files couldn't be loaded into Python, rather than
   * leaving it to fail later with "No such file" (EHQ-542). The step still
   * runs: it may not need those files, and an unreadable one shouldn't block it.
   * Names the first few, so a sync where everything fails stays one readable line.
   */
  private reportFileLoadFailures(context: ChunkContext): void {
    const failures = this.fileLoadingHelper?.takeFailures() ?? [];
    if (failures.length === 0) return;
    const shown = failures.slice(0, MAX_REPORTED_LOAD_FAILURES);
    const more = failures.length - shown.length;
    const list = more > 0 ? `${shown.join(', ')} (+${more} more)` : shown.join(', ');
    context.addMessage(`Some files could not be loaded into Python: ${list}`, 'warning');
  }

  /**
   * Unmount files using PythonFileSystemHelper
   */
  private async unmountFiles(filePaths?: string[]): Promise<void> {
    if (!this.fileSystemHelper) {
      return; // Made non-blocking for better error recovery
    }

    if (!this.python?.FS) {
      return;
    }

    try {
      await this.fileSystemHelper.unmountFiles(filePaths);
    } catch (error: any) {
      console.warn('Python service: unmount failed (non-blocking)', error);
    }
  }

  /**
   * Basic code formatter (fallback when Black is not available)
   * Delegates to PythonCodeFormatter
   */
  private basicFormatCode(code: string): string {
    return this.codeFormatter!.basicFormatCode(code);
  }


  private async processModuleDependencies(
    content: string,
    context: ChunkContext | any,
  ): Promise<void> {
    const { dependencies } = this.moduleManager.analyzeDependencies(content);

    if (dependencies.length) {
      for (const dep of dependencies) {
        // Strip namespace prefix if present for backward compatibility
        const nsPrefix = `${STEPS_NAMESPACE}.`;
        const depName = dep.startsWith(nsPrefix) ? dep.slice(nsPrefix.length) : dep;
        const chunk = this.stepService.stepList.find(
          (chunk: any) => this.moduleManager.sanitizeModuleName(chunk.name) === depName
        );

        if (!chunk) {
          throw new Error(`Dependency module ${dep} not found`);
        }

        // Register step as module and execute its code so functions/classes
        // are available when the consuming step does `from steps import <name>`
        const registration = await this.pythonModulesService.registerChunkAsModule(
          chunk as any, chunk.content, context
        );
        if (registration?.success && registration.moduleContext?.context_id) {
          await this.pythonRuntime.executeCode(
            chunk.content, null, registration.moduleContext.context_id
          );
        }
      };
    }
  }

  // ==================== PHASE 3 REFACTORING: EXTRACTED METHODS ====================

  /**
   * Step 1: Ensure the Python service is initialized and ready
   * Consolidates validation, initialization, and context setup (steps 1-13 from old flow)
   */
  private async ensureInitialized(context: ChunkContext): Promise<void> {
    // Validate context once
    if (!this.isContextValid(context)) {
      throw new Error('Invalid context provided to Python service');
    }

    if (!this.isLoaded) {
      context.addMessage('Initializing Python runtime...', 'info');
      await this.init();
    }

    // Verify critical components
    if (!this.fileSystemHelper) {
      throw new Error('Python service not properly initialized: missing FileSystem helper');
    }

    // Handle workflow/project context changes
    await this.checkAndHandleContextChange();

    // Check if chunk context has changed
    const contextChanged = this.hasContextChanged(context);

    // Update context references
    this.contextManager?.updateChunkContext(context);
    this.chunkData = context.getChunk();

    if (this.chunkData) {
      this.contextManager?.updateLastChunkId(this.chunkData.stepId);
    }

    // ✅ RESTORED: Set reload flag ONLY when context changes (not unconditionally)
    // This preserves HYBRID optimization while ensuring files reload when needed
    if (contextChanged) {
      this.manualReloadRequested = true;
    }
  }

  /**
   * Step 2: Synchronize project files with Pyodide filesystem
   * Consolidates file loading and verification (steps 14-22 and 38-45 from old flow)
   */
  private async syncProjectFiles(): Promise<void> {
    // Explicit check for fileSystemHelper to ensure type safety
    if (!this.fileSystemHelper) {
      throw new Error('FileSystemHelper is required for file operations');
    }

    const projectId = this.appStateService.projectId() || 0;
    const workflowId = this.appStateService.workflowId() || 0;

    // 1. Check for manual reload request (simple check)
    if (this.manualReloadRequested) {
      this.manualReloadRequested = false;

      // Get fresh file list from backend
      const files = await this.fileSystemHelper.getAllFilesFromAllSources(
        this.fileService,
        this.notebookFilesService,
        this.fileDataService
      );

      // SCRUM-166: Only run expensive stale metadata cleanup when file list actually changed
      // This avoids a full FS scan on every manual reload when files haven't changed
      const fileNamesChanged = this.hasFileListChanged(files);
      if (fileNamesChanged) {
        // Cleanup stale metadata (Journal + LocalforageService + Pyodide FS)
        // Compares backend files (source of truth) vs what's cached/loaded
        await this.cleanupHelper!.cleanupStaleMetadata(files, this.workingDir);
      }

      // Always unmount deleted files (works even if files = [])
      // This compares this.notebookFiles (old) vs files (new)
      await this.unmountMissingFiles(files);

      // Only load if there are files to load
      if (files.length > 0) {
        await this.loadAllFiles(files);
      }

      // Update notebookFiles AFTER operations complete
      this.notebookFiles = files;

      // That was a full load, so it covers a context change too. Record the
      // context and stop, or step 3 loads (and reports skipped files) again.
      if (this.isLoaded) {
        this.checkContextChanges();
        return;
      }
    }

    // 2. First run - load everything
    if (!this.isLoaded) {
      await this.loadAllFilesAndMarkLoaded(projectId, workflowId);
      return;
    }

    // 3. Check context changes
    const contextChanged = this.checkContextChanges();

    if (contextChanged.any) {
      const files = await this.fileSystemHelper.getAllFilesFromAllSources(
        this.fileService,
        this.notebookFilesService,
        this.fileDataService
      );

      // Always unmount deleted files FIRST (compare old vs new)
      await this.unmountMissingFiles(files);

      if (files.length === 0) {
        this.notebookFiles = files;  // Update state before return
        return;
      }

      // Handle based on WHAT changed
      if (contextChanged.needsFullReload) {
        await this.loadAllFiles(files);
      } else if (contextChanged.needsProjectFilesReload) {
        await this.loadProjectFilesOnly(files);
      } else if (contextChanged.needsWorkflowFilesReload) {
        await this.loadWorkflowFilesOnly(files);
      }

      // Update notebookFiles AFTER operations complete
      this.notebookFiles = files;

      return;
    }
  }

  /**
   * Load all files (packages + modules + data)
   * Delegates to PythonFileLoadingHelper
   */
  private async loadAllFiles(files: NotebookFile[]): Promise<void> {
    await this.fileLoadingHelper!.loadAllFiles(
      files,
      (content, filePath) => this.processLoadingPackagesFromImports(content, filePath)
    );
  }

  /**
   * Load project files only (modules + data, skip packages)
   * Delegates to PythonFileLoadingHelper
   */
  private async loadProjectFilesOnly(files: NotebookFile[]): Promise<void> {
    await this.fileLoadingHelper!.loadProjectFilesOnly(
      files,
      (content, filePath) => this.processLoadingPackagesFromImports(content, filePath)
    );
  }

  /**
   * Load workflow files only (data files)
   * Delegates to PythonFileLoadingHelper
   */
  private async loadWorkflowFilesOnly(files: NotebookFile[]): Promise<void> {
    await this.fileLoadingHelper!.loadWorkflowFilesOnly(files);
  }

  /**
   * Load all files and mark service as loaded
   * Helper method to avoid code duplication in syncProjectFiles()
   */
  private async loadAllFilesAndMarkLoaded(_projectId: number, _workflowId: number): Promise<void> {
    // Always load from backend (no cache)
    const files = await this.fileSystemHelper!.getAllFilesFromAllSources(
      this.fileService,
      this.notebookFilesService,
      this.fileDataService
    );

    // ⭐ Clean up files deleted from backend (fe813d8 behavior)
    // Always unmount (works even if files = [])
    await this.unmountMissingFiles(files);

    if (files.length > 0) {
      await this.loadAllFiles(files);
    }

    // Update notebookFiles AFTER operations complete
    this.notebookFiles = files;
    this.isLoaded = true;
  }

  /**
   * Step 3: Prepare code for execution
   * Consolidates content processing, validation, and dependency setup (steps 23-36 and 41-43 from old flow)
   */
  private async prepareCode(context: ChunkContext): Promise<string | null> {
    // Get and process chunk content
    let content = this.getContentWithVariablesReplaced(context.getChunkContent()).trim();

    // Transform for real-time ehq.log() output (before syntax validation)
    content = this.codeFormatter!.transformForRealTimeLogging(content);

    if (!content) {
      this.chunkData = null;
      return null;
    }

    // Validate Python syntax
    const syntaxValidation = await this.validateSyntax(content);
    if (!syntaxValidation.isValid) {
      // Report warning but continue execution
      let errorMessage = 'Python syntax error';
      if (syntaxValidation.error) {
        errorMessage += `: ${syntaxValidation.error}`;
      }
      if (syntaxValidation.suggestions && syntaxValidation.suggestions.length > 0) {
        errorMessage += '\n💡 Suggestions: ' + syntaxValidation.suggestions.join(', ');
      }
      context.addMessage(errorMessage, 'warning');
      context.addOutput(`Syntax Warning: ${errorMessage}`, 'error', 'python');
    }

    // Python environment already configured in initialize()
    // No need to reconfigure on every run

    // Add global variables
    this.addGlobalVariables(this.chunkData);

    // Allow Python stdlib logging to write to step logs (ehq.log bridge).
    content = PythonCodeFormatter.injectLoggerSetup(content);

    // Register external JS modules
    content = this.registerExternalJSModules(content);

    // Always add empty comment at the end to keep last-line parsing stable.
    // module_system ignores trailing comments when detecting last expressions.
    if (!content.trim().endsWith('#')) {
      content = content.trimEnd() + '\n#';
    }

    // Ensure module system is initialized
    if (!this.moduleManager.isModuleSystemReady()) {
      await this.moduleManager.setupModuleSystem();
    }

    // Process all dependencies in order
    // NOTE: processGlobalModules() removed to prevent duplication
    // Module List files already loaded via getAllFilesFromAllSources() → loadAllModules()
    await this.processModuleDependencies(content, context);
    await this.processAllImports(content);

    // Analyze imports for missing modules
    if (this.importsHelper) {
      const importAnalysis = this.importsHelper.analyzeImports(content);
      if (importAnalysis.missingModules.length > 0) {
        const suggestions = importAnalysis.missingModules.map(mod =>
          this.importsHelper?.getImportSuggestions(mod) || []
        ).flat();

        if (suggestions.length > 0) {
          context.addMessage(
            `Warning: Missing modules: ${importAnalysis.missingModules.join(', ')}. ` +
            `Consider installing: ${[...new Set(suggestions)].join(', ')}`,
            'warning'
          );
        }
      }
    }

    return content;
  }

  /**
   * Step 4: Execute the prepared code
   * Consolidates module registration and execution (steps 37 and 46-48 from old flow)
   */
  private async executePythonCode(code: string, context: ChunkContext): Promise<any> {
    // Start clean so a controlled-halt flag from a prior step can't leak in.
    resetControlHalt();

    // Register chunk as module
    const registration = await this.pythonModulesService.registerChunkAsModule(
      this.chunkData,
      code,
      context as any
    );

    // Execute code with error handling
    try {
      const result = await this.pythonRuntime.executeCode(
        code,
        null,
        registration.moduleContext.context_id
      );
      // Honor a controlled halt even if the Python code swallowed the thrown
      // exception (parity with enoch's post-step check): a set flag means the
      // step aborted/retried/needs-review regardless of the success return.
      const halt = consumeControlHalt();
      if (halt) {
        return { controlHalt: halt, output: null };
      }
      return result;
    } catch (executionError: any) {
      // A controlled halt (EHQ.abortJob/requestRetry/requestReview) is not a
      // crash — present it as a clean, neutral outcome (parity with enoch, which
      // marks the job Aborted/Retry/NeedsReview rather than Failed), not a red
      // "Execution failed".
      const halt = consumeControlHalt();
      if (halt) {
        return { controlHalt: halt, output: null };
      }
      // Return error as result instead of throwing
      return {
        error: `Execution failed: ${executionError.message}`,
        output: null
      };
    }
  }

  /**
   * Step 5: Format and return the execution result
   * Consolidates result processing and cleanup (steps 49-59 from old flow)
   */
  private async processResult(result: any, context: ChunkContext, hasSyntaxError: boolean = false, hadStreamedOutput: boolean = false): Promise<Chunk | undefined> {
    // Extract global variables
    let globalVariables: any;
    if (this.variablesHelper) {
      const dataResult = this.variablesHelper.getDataVariable();
      globalVariables = dataResult.success ? dataResult.value : {};
    } else {
      // Fallback to original implementation
      globalVariables = this.python.globals.get('data').toJs();
      this.python.globals.get('data').destroy();
      globalVariables = GeneralHelpers.fromMapToObject(globalVariables);
    }

    // Add variables to notebook
    this.notebookVariablesService.addVarsFromRawToList(
      globalVariables,
      this.chunkData,
      this.projectVariablesService.variableList
    );

    // Cleanup external JS modules
    this.unregisterExternalJSModules(this.lastProcessedContent || '');

    // PROT-810 / EHQ-104: Sync generated files from Pyodide FS to backend.
    // Browser collects changed files once; CacheService/NotebookDefaultFilesService
    // batch-upsert them via a single API call per file class (see syncFilesToBackend).
    try {
      const innerPyodideFiles = this.getMountedFiles();

      if (innerPyodideFiles.size > 0) {
        await this.syncPyodideFilesToBackend(innerPyodideFiles);
      }
    } catch (error: any) {
      console.warn('Python service: failed to sync Pyodide files', error);
    }

    // Format output based on result
    const trimmedOutput = result.output?.trim() ?? '';

    if (result.controlHalt) {
      // Controlled halt (EHQ.abortJob/requestRetry/requestReview) — rendered as a
      // neutral warning, not a red execution error. Parity with enoch's
      // Aborted/Retry/NeedsReview terminals (vs Failed).
      context.reportControlHalt(result.controlHalt, 'python');
      context.setBusy(false);
    } else if (result.error) {
      // Error replaces all output (don't append)
      context.addOutput(result.error, 'error', 'python');
      context.setBusy(false, 'error');
    } else if (result.lastExpressionResult !== undefined && result.lastExpressionResult !== null) {
      // Filter out Pyodide JsProxy/Promise objects (void bridge returns like displayTable).
      // These are not real user output — they're JS Promise wrappers from ehq.* methods.
      const lastExpr = result.lastExpressionResult;
      const isVoidProxy = typeof lastExpr === 'object' &&
        (typeof lastExpr.then === 'function' || typeof lastExpr.toJs === 'function');
      if (!isVoidProxy) {
        // Real expression result (e.g. print("DEBUG") + expression 42 → lastOutput = "DEBUG\n42")
        context.addOutput(lastExpr, 'success', 'python', true);
      } else if (!hadStreamedOutput && trimmedOutput) {
        // Bridge call was last line but we have captured print output
        context.addOutput(trimmedOutput, 'success', 'python');
      }
    } else if (!hasSyntaxError && !hadStreamedOutput) {
      context.addOutput(trimmedOutput || 'Code executed successfully', 'success', 'python');
    }

    // Return chunk safely
    if (context && typeof context.getChunk === 'function') {
      return context.getChunk();
    } else {
      return undefined;
    }
  }

  /**
   * Handle execution errors uniformly
   */
  private handleExecutionError(error: any, context: ChunkContext): Chunk | undefined {

    if (context && typeof context.addMessage === 'function') {
      context.addMessage('Script execution failed: ' + error.message, 'danger');
      context.addOutput(error.message, 'error', 'python');
    }

    if (context && typeof context.getChunk === 'function') {
      return context.getChunk();
    }

    return undefined;
  }

  // Store last processed content for cleanup
  private lastProcessedContent: string = '';

  /**
   * PHASE 1: Install all packages first (.whl files)
   * Delegates to PythonFileLoadingHelper
   */
  private async installAllPackages(files: NotebookFile[]): Promise<void> {
    try {
      await this.fileLoadingHelper!.installAllPackages(files);
    } catch (error: any) {
      console.warn('Python service: Error installing packages', error);
      // Continue execution - errors are logged but don't stop the process
    }
  }

  /**
   * PHASE 2: Load all Python modules second (.py files)
   * Delegates to PythonFileLoadingHelper
   */
  private async loadAllModules(files: NotebookFile[]): Promise<void> {
    // const moduleFiles = files.filter(f =>
    //   GeneralHelpers.fileExtensionFromString(f.name) === FILE_EXTENSIONS.py
    // );

    // Load modules to filesystem (matches Downloads behavior)
    try {
      await this.fileLoadingHelper!.loadAllModules(
        files,
        (content, filePath) => this.processLoadingPackagesFromImports(content, filePath)
      );
    } catch (error: any) {
      console.warn('Python service: Error loading modules', error);
      // Continue execution - errors are logged but don't stop the process
    }
    // ✅ CRITICAL FIX: Register module list files in module_system
    // Module list files (from Project Files sidebar) need to be registered
    // in Python's module system to be importable via "import module_name"
    // This is SEPARATE from FileModule List registration (done in helper above)
    // for (const file of moduleFiles) {
    //   // Check if this is a module list file (has versionId or source='moduleList')
    //   const isModuleListFile = (file as any).versionId || (file as any).source === 'moduleList';

    //   if (isModuleListFile) {
    //     const content = (file as any).content;
    //     if (content) {
    //       try {
    //         await this.moduleManager.registerModule(file.name, content);
    //         console.log(`✅ Registered module in module_system: ${file.name}`);
    //       } catch (error: any) {
    //         console.error(`❌ Failed to register module ${file.name}:`, error);
    //         // Continue with other modules - non-fatal
    //       }
    //     }
    //   }
    // }
  }

  /**
   * PHASE 3: Load all data files last (.csv, .json, etc.)
   * Delegates to PythonFileLoadingHelper
   */
  private async loadAllDataFiles(files: NotebookFile[]): Promise<void> {
    try {
      await this.fileLoadingHelper!.loadAllDataFiles(files);
    } catch (error: any) {
      console.warn('Python service: Error loading data files', error);
      // Continue execution - errors are logged but don't stop the process
    }
  }

  /**
   * Starts a Python REPL/debugger session
   * Creates a persistent PyodideConsole instance for interactive execution
   *
   * @param chunkContext - The chunk execution context
   * @returns PythonDebugger object containing console helper and context
   */
  public async startDebugger(
    chunkContext: any
  ): Promise<any> {
    const { PyodideConsoleHelper } = await import('./execution/python-console.helper');

    await this.ensureInitialized(chunkContext);

    // The console runs user code, so default/ and project/ are read-only here
    // as in a step run (EHQ-545). Opened before any run, they never were.
    this.fsProxy?.enablePermissions();

    // Align debugger behavior with step execution logger support.
    this.configurePythonLoggerForDebugger();

    // Create console helper
    const consoleHelper = new PyodideConsoleHelper();
    await consoleHelper.initialize(this.python);

    // Setup output capture for print() statements
    // NOTE: These callbacks handle ONLY print() output, not expression results
    // Results are handled directly in ConsoleDisplayComponent to avoid duplicate writes
    this.python.setStdout({
      batched: (msg: string) => {
        if (this.isContextValid(chunkContext)) {
          chunkContext.addLog(msg, 'info');
        }
      }
    });

    this.python.setStderr({
      batched: (msg: string) => {
        if (this.isContextValid(chunkContext)) {
          chunkContext.addLog(msg, 'error');
        }
      }
    });

    // Inject notebook variables into 'data' global
    const globalDataVariables: { [key: string]: string } = {};

    if (
      Array.isArray(this.notebookVariablesService.variableList) &&
      this.notebookVariablesService.variableList.length > 0
    ) {
      for (const element of this.notebookVariablesService.variableList) {
        globalDataVariables[element.name] = element.value;
      }
    }

    // Set data variable using helper
    if (this.variablesHelper && Object.keys(globalDataVariables).length > 0) {
      this.variablesHelper.setDataVariable(globalDataVariables);
    }

    return {
      consoleHelper,
      chunk: chunkContext.chunk,
      context: chunkContext
    };
  }

  /**
   * Executes code in the Python REPL/debugger session
   * Supports multiline statements with automatic incomplete detection
   *
   * @param pythonDebugger - The Python debugger instance
   * @param code - Python code to execute
   * @param chunkContext - The chunk execution context
   * @returns Execution result with syntax check status
   */
  public async executeInDebugger(
    pythonDebugger: any,
    code: string,
    _chunkContext: any
  ): Promise<{ result?: any; error?: string; needMoreInput?: boolean }> {
    try {
      const future = await pythonDebugger.consoleHelper.push(code);

      // Check if we need more input (multiline statement)
      if (future.syntax_check === 'incomplete') {
        return { needMoreInput: true };
      }

      // Check for syntax errors
      if (future.syntax_check === 'syntax_error') {
        const errorMsg = future.exception?.toString() || 'Syntax error';
        return { error: errorMsg };
      }

      // Statement is complete - return result
      return { result: future.result };

    } catch (error: any) {
      return { error: error.message || String(error) };
    }
  }

  /**
   * Setup real-time output streaming for Python execution
   * Redirects Python stdout/stderr to ChunkContext output for immediate display
   *
   * Used for both:
   * - Regular execution: print() → addOutput() → Output tab
   * - Debugger mode: print() → addLog() → Logs tab (configured separately)
   *
   * @param context - The chunk execution context
   */
  private setupOutputStreaming(context: ChunkContext): void {
    // Setup stdout handler for print() statements → Output tab
    this.python.setStdout({
      batched: (msg: string) => {
        if (this.isContextValid(context)) {
          // CRITICAL: Clear output on FIRST output of new execution
          // This keeps old output visible until new output starts flowing
          if (this.firstOutputOfRun) {
            context.clearOutput();
            this.firstOutputOfRun = false;
          }

          // CRITICAL FIX: Use append=true to accumulate print() output
          context.addOutput(msg, 'success', 'python', true);
        }
      }
    });

    // Setup stderr handler for error output → Output tab
    this.python.setStderr({
      batched: (msg: string) => {
        if (this.isContextValid(context)) {
          // CRITICAL: Clear output on FIRST error output
          if (this.firstOutputOfRun) {
            context.clearOutput();
            this.firstOutputOfRun = false;
          }

          // CRITICAL FIX: Use append=true to accumulate stderr output
          context.addOutput(msg, 'error', 'python', true);
        }
      }
    });
  }

  /**
   * Cleanup output streaming handlers
   * Resets stdout/stderr to no-op handlers
   */
  private cleanupOutputStreaming(): void {
    // Reset to no-op handlers
    this.python.setStdout({ batched: () => { } });
    this.python.setStderr({ batched: () => { } });
  }

  /**
   * Disposes the Python debugger and cleans up resources
   * Resets stdout/stderr handlers and destroys the console instance
   *
   * @param pythonDebugger - The Python debugger instance to dispose
   */
  public disposeDebugger(pythonDebugger: any): void {
    pythonDebugger.consoleHelper.destroy();

    // Reset stdout/stderr to default (no-op)
    this.cleanupOutputStreaming();
  }

}
